import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { isRecord } from '../utils/backendData';

type LocationState = {
  trade?: unknown;
};

type BuyTarget = {
  opened?: boolean;
  price?: string | number;
  status?: string;
  role?: string;
  type?: string;
};

type SellTarget = {
  opened?: boolean;
  price?: string | number;
  status?: string;
  role?: string;
  internal?: boolean;
  type?: string;
  fraction?: number;
};

type StopLoss = {
  price?: string | number;
  type?: string;
  status?: string;
  enabled?: boolean;
  mode?: string;
  opened?: boolean;
  side?: string;
};

type Strategy = {
  is_pro?: boolean;
  name?: string;
  x_pro?: number;
};

type UsedCoins = {
  currentQuantity?: number;
  wished_quantity?: number;
};

type Precisions = {
  lotSize?: number;
};

type Notification = {
  content?: string;
};

type TradeData = {
  _id?: string;
  id?: string;
  state?: string;
  result?: string;
  profit?: number;
  tradeLeverage?: string;
  is_long?: boolean;
  symbol?: string;
  exchangeClientId?: string;
  exchangeClientName?: string;
  exchange_client?: string;
  startTime?: string;
  endTime?: string;
  auto_Trade?: {
    channelName?: string;
    channel_id?: number;
    exchangeClientName?: string;
  } | null;
  stop_loss?: StopLoss | null;
  buy_targets?: BuyTarget[];
  sell_targets?: SellTarget[];
  strategy?: Strategy | null;
  used_coins?: UsedCoins | null;
  notifications?: Notification[];
  precisions?: Precisions | null;
};

function isBuyTarget(value: unknown): value is BuyTarget {
  return isRecord(value);
}

function isSellTarget(value: unknown): value is SellTarget {
  return isRecord(value);
}

function isNotification(value: unknown): value is Notification {
  return isRecord(value);
}

const formatDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const formatNumber = (value?: number | string, digits = 2) => {
  if (value === undefined || value === null || value === '') return '—';
  const numeric = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(numeric)) return String(value);
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(numeric);
};

const parseSymbol = (symbol?: string) => {
  if (!symbol) return { base: '—', quote: '—' };
  const delimiter = symbol.includes('-')
    ? '-'
    : symbol.includes('/')
      ? '/'
      : symbol.includes('_')
        ? '_'
        : null;
  if (delimiter) {
    const [base, quote] = symbol.split(delimiter);
    return { base: base || '—', quote: quote || '—' };
  }
  return { base: symbol, quote: '—' };
};

const targetStatus = (status?: string) => {
  if (!status) return '';
  switch (status) {
    case 'placed':
      return 'placed☑️';
    case 'filled':
      return 'filled✅';
    case 'canceled':
      return 'canceled✖️';
    default:
      return status;
  }
};

const getTargetQuantity = (
  fraction?: number,
  wishedQuantity?: number,
  lotSize?: number,
) => {
  if (fraction === undefined || wishedQuantity === undefined) return null;
  const raw = wishedQuantity * fraction;
  if (!lotSize || lotSize <= 0) return raw;
  return Math.floor(raw / lotSize) * lotSize;
};

export default function Trade() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state || {}) as LocationState;
  const trade = (state.trade ?? null) as TradeData | null;
  const tradeId = trade?._id ?? trade?.id ?? '—';
  const buyTargets: BuyTarget[] = Array.isArray(trade?.buy_targets)
    ? (trade?.buy_targets ?? []).filter(isBuyTarget)
    : [];
  const sellTargets: SellTarget[] = Array.isArray(trade?.sell_targets)
    ? (trade?.sell_targets ?? []).filter(isSellTarget)
    : [];
  const notifications: Notification[] = Array.isArray(trade?.notifications)
    ? (trade?.notifications ?? []).filter(isNotification)
    : [];
  const isFutures = (trade?.exchange_client ?? '').toLowerCase().includes('futures');
  const tradeLeverage = trade?.tradeLeverage ?? '—';
  const { base, quote } = parseSymbol(trade?.symbol);
  const wishedQuantity = trade?.used_coins?.wished_quantity ?? 0;
  const currentQuantity = trade?.used_coins?.currentQuantity ?? 0;
  const entryPrice = buyTargets[0]?.price;
  const entryPriceNumber = entryPrice ? Number(entryPrice) : 0;
  const startPositionSize = wishedQuantity * entryPriceNumber;
  const currentPositionSize = currentQuantity * entryPriceNumber;
  const numbersEmoji = ['1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣', '🔟'];

  const getEntrySystem = () => {
    if (buyTargets.find((target) => target.type === 'MARKET')) return '⚡️MARKET';
    if (buyTargets.find((target) => target.type === 'STOP_LOSS_LIMIT')) return '🌊BREAKOUT';
    if (buyTargets.length) return '🪨LIMIT';
    return '—';
  };

  const getProtectionSystem = () => {
    if (!trade?.stop_loss) return '⚔️No Protection System';
    const mode = trade.stop_loss.mode?.toLowerCase() ?? '';
    if (mode.includes('hidden')) return '🔰Hidden SL';
    return '🔰Visible';
  };

  const getTradingStrategy = () => {
    if (trade?.strategy?.is_pro) return '⬛️MASTER';
    if (trade?.strategy?.name === 'basic') return '🟦BASIC';
    if (trade?.strategy?.name) return '🟫PRO';
    return '—';
  };

  console.log('Trade data:', trade);

  return (
    <Box
      component="main"
      sx={{
        minHeight: 'calc(100vh - 64px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        bgcolor: 'background.default',
        p: 2,
        pt: 4,
      }}
    >
      <Paper elevation={3} sx={{ p: 4, maxWidth: 1100, width: '100%' }}>
        <Stack
          spacing={2.5}
          sx={{
            alignItems: 'center',
            textAlign: 'left',
            '& .MuiTypography-root': { textAlign: 'left' },
            '& .MuiTypography-h5': { fontSize: 24, textAlign: 'center' },
            '& .MuiTypography-subtitle1': { fontSize: 19, fontWeight: 700 },
            '& .MuiTypography-body1': { fontSize: 17 },
            '& .MuiTypography-body2': { fontSize: 16 },
            '& > *': { width: '100%', maxWidth: 900 },
            '& .MuiDivider-root': { width: '100%', maxWidth: 900, borderColor: 'grey.100' },
          }}
        >
          <Box sx={{ alignSelf: 'stretch', display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: '999px',
                textTransform: 'none',
                fontSize: 12,
                fontWeight: 600,
                px: 4,
                py: 0.25,
                minWidth: 140,
                color: 'grey.800',
                borderColor: 'grey.300',
                bgcolor: 'grey.100',
                boxShadow: '0 6px 14px rgba(17, 24, 39, 0.08)',
                '&:hover': {
                  bgcolor: 'grey.200',
                  borderColor: 'grey.400',
                },
              }}
            >
              BACK
            </Button>
          </Box>
          
          <Typography variant="h5" align="center">
            📉📈Trade
          </Typography>

          {!trade ? (
            <Alert severity="info">No trade data found.</Alert>
          ) : (
            <>
              <Stack spacing={1}>
                <Typography variant="body1">
                  💸 <strong>Profit/Loss:</strong> {trade.profit ?? 0}%
                </Typography>
                <Typography variant="body1">
                  🏛 <strong>Exchange:</strong> {trade.exchangeClientName ?? '—'}{' '}
                  {isFutures ? 'FUTURES' : 'SPOT'}
                </Typography>
                <Typography variant="body1">
                  👤 <strong>Account:</strong> {trade.exchangeClientName ?? '—'}
                </Typography>
                <Typography variant="body1">
                  🪙 <strong>Ticker:</strong> {trade.symbol ?? '—'}
                </Typography>
                <Typography variant="body1">
                  📊 <strong>Position:</strong>{' '}
                  {isFutures ? `🟩FUTURES ${trade.is_long ? 'LONG' : 'SHORT'}` : '🟩SPOT BUY'}
                </Typography>
              </Stack>

              <Stack spacing={0.75}>
                <Typography variant="subtitle1">🏁 Entry System</Typography>
                <Typography variant="body2">{getEntrySystem()}</Typography>
                {buyTargets.length ? (
                  buyTargets.map((entryTarget, index) => {
                    const status = targetStatus(entryTarget.status);
                    const suffix = status ? ` ${status}` : '';
                    if (entryTarget.type === 'MARKET') {
                      return (
                        <Typography variant="body2" key={`buy-${index}`}>
                          ⚡️{entryTarget.price ?? '—'}
                          {suffix}
                        </Typography>
                      );
                    }
                    if (entryTarget.type === 'STOP_LOSS_LIMIT') {
                      return (
                        <Typography variant="body2" key={`buy-${index}`}>
                          🌊{numbersEmoji[index] ?? ''} {entryTarget.price ?? '—'}
                          {suffix}
                        </Typography>
                      );
                    }
                    return (
                      <Typography variant="body2" key={`buy-${index}`}>
                        🪨{numbersEmoji[index] ?? ''} {entryTarget.price ?? '—'}
                        {suffix}
                      </Typography>
                    );
                  })
                ) : (
                  <Typography variant="body2">—</Typography>
                )}
              </Stack>

              <Stack spacing={0.75}>
                <Typography variant="subtitle1">🎯 Target System</Typography>
                <Typography variant="body2">
                  {sellTargets.length ? '🎯TARGETS' : '▶️OPEN TARGET'}
                </Typography>
                {sellTargets.length ? (
                  sellTargets.map((target, index) => {
                    const status = targetStatus(target.status);
                    const suffix = status ? ` ${status}` : '';
                    const targetQuantity = getTargetQuantity(
                      target.fraction,
                      wishedQuantity,
                      trade?.precisions?.lotSize,
                    );
                    return (
                      <Typography variant="body2" key={`sell-${index}`}>
                        🎯{numbersEmoji[index] ?? ''} {target.price ?? '—'}
                        {suffix}
                        {targetQuantity !== null
                          ? ` | ${formatNumber(targetQuantity, 6)} ${base}`
                          : ''}
                      </Typography>
                    );
                  })
                ) : (
                  <Typography variant="body2">—</Typography>
                )}
              </Stack>

              {isFutures ? (
                <>
                  <Divider />
                  <Typography variant="body1">
                    💳 <strong>Leverage:</strong> {tradeLeverage}
                  </Typography>
                </>
              ) : null}


              <Stack spacing={0.75}>
                <Typography variant="subtitle1">🔰 Protection System</Typography>
                <Typography variant="body2">{getProtectionSystem()}</Typography>
                {trade.stop_loss ? (
                  <Typography variant="body2">
                    🔰1️⃣ {trade.stop_loss.price ?? '—'}{' '}
                    {targetStatus(trade.stop_loss.status)}
                  </Typography>
                ) : null}
              </Stack>

              <Stack spacing={0.5}>
                <Typography variant="body2">
                  💰 <strong>Coins:</strong> {formatNumber(wishedQuantity, 6)} {base}
                </Typography>
                <Typography variant="body2">
                  💰 <strong>Starting Position Size:</strong>{' '}
                  {formatNumber(startPositionSize, 2)} {quote}
                </Typography>
                <Typography variant="body2">
                  💰 <strong>Current Position Size:</strong>{' '}
                  {formatNumber(currentPositionSize, 2)} {quote}
                </Typography>
              </Stack>

              <Stack spacing={0.75}>
                <Typography variant="subtitle1">👑 Trading Strategy</Typography>
                <Typography variant="body2">{getTradingStrategy()}</Typography>
                {trade?.strategy?.is_pro ? (
                  <Typography variant="body2">
                    👑 <strong>Trailing:</strong> {formatNumber((trade.strategy.x_pro ?? 0) * 100, 2)}%
                  </Typography>
                ) : null}
              </Stack>

              <Typography variant="body1">
                {trade.auto_Trade
                  ? '🛩AutoTrade ⚠️only channel admin can edit this trade'
                  : '✋MyPersonalTrade'}
              </Typography>
              {trade.auto_Trade ? (
                <Typography variant="body2">
                  <strong>Channel:</strong> {trade.auto_Trade.channelName ?? '—'}
                </Typography>
              ) : null}

              {notifications.length ? (
                <>
                  <Divider />
                  <Typography variant="subtitle1">ℹ️ Recent trade notifications</Typography>
                  {notifications.map((info, index) => (
                    <Typography variant="body2" key={`note-${index}`}>
                      {info.content ?? '—'}
                    </Typography>
                  ))}
                </>
              ) : null}

              <Divider />

              <Stack spacing={0.5}>
                <Typography variant="body2">Result: {trade.result ?? '—'}</Typography>
                <Typography variant="body2">Started: {formatDate(trade.startTime)}</Typography>
                <Typography variant="body2">Ended: {formatDate(trade.endTime)}</Typography>
                <Typography variant="body2">
                  Exchange Client ID: {trade.exchangeClientId ?? '—'}
                </Typography>
              </Stack>
            </>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
